import React, { useState, useEffect, useCallback } from 'react';
import { Box, Tooltip } from '@mui/material';
import { Add } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { GridRowSelectionModel } from '@mui/x-data-grid/models/gridRowSelectionModel';
import { useEvents } from '@/hooks/useEvents';
import { usePermissions, getActionTooltip, useSnackbarNotification } from '@/hooks';
import { DataGridContainer, StandardActions } from '@/components';
import { Event } from '@/api/bol';
import type { LogisticsObjectEntity } from '@/types/logisticsObject';
import {
  createEventsColumns,
  defaultEventsColumnVisibilityModel,
} from './EventsColumns';
import { EventsFilters } from './EventsFilters';
import EventsFormDialog from './EventsFormDialog';
import EventsDeleteDialog from './EventDeleteDialog';

interface EventsTabProps {
  logisticsObjectType: LogisticsObjectEntity;
  objectId: string;
  memberIdentifier?: string;
  onEventSelected?: (event: Event | null) => void;
  selectedEventId?: string | null;
}

const EventsTab: React.FC<EventsTabProps> = ({
  logisticsObjectType,
  objectId,
  memberIdentifier,
  onEventSelected,
  selectedEventId,
}) => {
  const { can } = usePermissions();
  const { showSuccess, showError } = useSnackbarNotification();
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(
    defaultEventsColumnVisibilityModel
  );
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  const {
    events,
    loading,
    filters,
    appliedFilters,
    setFilters,
    getEvents,
    createEvent,
    deleteEvent,
    applyFilters,
    clearFilters,
    hasUnappliedChanges,
    total,
  } = useEvents(logisticsObjectType, objectId);

  useEffect(() => {
    if (selectedEventId !== undefined && selectedEventId !== selectedRowId) {
      setSelectedRowId(selectedEventId ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId]);

  useEffect(() => {
    void getEvents(paginationModel.page, paginationModel.pageSize);
  }, [paginationModel.page, paginationModel.pageSize, appliedFilters, getEvents]);

  const handleRefresh = () => {
    void getEvents(paginationModel.page, paginationModel.pageSize);
  };

  const handleCreateEvent = async (
    eventData: Parameters<typeof createEvent>[0],
    selectedMemberIdentifier: string,
  ) => {
    try {
      await createEvent(eventData, selectedMemberIdentifier);
      showSuccess('Event created successfully');
      setCreateDialogOpen(false);
    } catch (error) {
      showError('Failed to create event');
      console.error('Error creating event:', error);
    }
  };

  const handleDeleteClick = (event: Event) => {
    setSelectedEvent(event);
    setDeleteDialogOpen(true);
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent?.id || !memberIdentifier) return;

    try {
      await deleteEvent(selectedEvent.id, memberIdentifier);
      showSuccess('Event deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedEvent(null);
    } catch (error) {
      showError('Failed to delete event');
      console.error('Error deleting event:', error);
    }
  };

  const handleRowSelectionChange = useCallback((selectionModel: GridRowSelectionModel) => {
    const newRowId = selectionModel.length > 0 ? String(selectionModel[0]) : null;
    setSelectedRowId(newRowId);
    const selectedEvent = newRowId
      ? events.find(e => e.id === newRowId) || null
      : null;
    onEventSelected?.(selectedEvent);
  }, [events, onEventSelected]);

  const handleApplyFilters = () => {
    setPaginationModel({ ...paginationModel, page: 0 });
    applyFilters();
  };

  const handleClearFilters = () => {
    setPaginationModel({ ...paginationModel, page: 0 });
    clearFilters();
  };

  const handleCopy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess('Copied to clipboard');
    } catch {
      showError('Failed to copy to clipboard');
    }
  }, [showSuccess, showError]);

  const columns = createEventsColumns({
    onCopy: handleCopy,
    onDelete: handleDeleteClick,
    canDelete: can.updateLogisticsObject,
    deleteTooltip: getActionTooltip('updateLogisticsObject'),
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <EventsFilters
          filters={filters}
          appliedFilters={appliedFilters}
          onFiltersChange={setFilters}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          hasUnappliedChanges={hasUnappliedChanges}
        />

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGridContainer
          rows={events}
          columns={columns}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={total}
          paginationMode="server"
          onRowSelectionChange={handleRowSelectionChange}
          rowSelectionModel={selectedRowId ? [selectedRowId] : []}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          columnOrderStorageKey="events-columns"
          selectedRowId={selectedRowId}
          getRowId={(row) => row.id}
          sx={{ flex: 1 }}
          actionButtons={
            <>
              <Tooltip title={!can.updateLogisticsObject ? getActionTooltip('updateLogisticsObject') : ''}>
                <span>
                  <StandardActions
                    onAdd={() => setCreateDialogOpen(true)}
                    addButtonText="Create Event"
                    addButtonIcon={<Add />}
                    disabled={!can.updateLogisticsObject}
                  />
                </span>
              </Tooltip>
              <StandardActions
                onRefresh={handleRefresh}
                refreshTooltip="Refresh events list"
              />
            </>
          }
        />
      </Box>

      <EventsFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateEvent}
      />

      <EventsDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteEvent}
        event={selectedEvent}
      />
    </Box>
    </LocalizationProvider>
  );
};

export default EventsTab;
